import React, { useEffect, useState } from "react";
import {
  TextInput,
  DropDown,
  Checkbox,
  Button,
} from "@relentless/rmds/v1/relentless.esm.js";
import { getContactCaptcha, submitContactForm } from "../lib/api";

const reasons = ["General", "Project Inquiry", "Speaking", "Other"];

export default function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [agree, setAgree] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const loadCaptcha = async () => {
    try {
      setCaptchaLoading(true);
      const response = await getContactCaptcha();
      setCaptcha(response?.data || null);
      setCaptchaAnswer("");
    } catch (_) {
      setCaptcha(null);
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const onSubmit = async () => {
    if (!name || !email || !message) {
      setStatus({
        type: "error",
        message: "Please fill in name, email, and message.",
      });
      return;
    }
    if (!agree) {
      setStatus({
        type: "error",
        message: "Please agree to the terms and privacy policy.",
      });
      return;
    }
    if (!captcha || !captchaAnswer.trim()) {
      setStatus({
        type: "error",
        message: "Please complete the captcha challenge.",
      });
      return;
    }
    try {
      setSubmitting(true);
      setStatus({ type: "", message: "" });
      await submitContactForm({
        name,
        email,
        reason,
        message,
        agree,
        website: honeypot,
        captchaToken: captcha?.token || "",
        captchaAnswer,
      });
      setStatus({
        type: "success",
        message: "Thanks. Your message was sent successfully.",
      });
      setName("");
      setEmail("");
      setReason("");
      setMessage("");
      setAgree(false);
      setHoneypot("");
      await loadCaptcha();
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.message || "We could not send your message right now.",
      });
      await loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-form">
      <div className="contact-fields">
        <TextInput
          label="Your Name"
          placeholder="Jane Doe"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextInput
          label="Email"
          type="email"
          placeholder="jane@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <DropDown
          label="Reason"
          placeholder="Select a reason"
          value={reason}
          options={reasons}
          onSelect={(val) => setReason(val)}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <div className="grid gap-md mt-md">
        <TextInput
          label="Message"
          as="textarea"
          rows={5}
          placeholder="How can we help?"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Checkbox
          label="I agree to the terms and privacy policy"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
        />
        <div className="contact-captcha">
          <div className="contact-captcha__prompt-row">
            <TextInput
              label={captcha?.prompt || "Captcha"}
              placeholder={captchaLoading ? "Loading challenge..." : "Enter answer"}
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
              disabled={captchaLoading || submitting || !captcha}
            />
            <Button
              variant="secondary"
              size="md"
              onClick={loadCaptcha}
              label="Refresh"
              disabled={captchaLoading || submitting}
            />
          </div>
        </div>
        {status.message ? (
          <p
            className={`contact-status contact-status--${status.type || "info"}`}
          >
            {status.message}
          </p>
        ) : null}
      </div>
      <div className="grid row justify-end mt-md">
        <Button
          variant="primary"
          size="md"
          onClick={onSubmit}
          label={submitting ? "Sending..." : "Send"}
          disabled={submitting}
        />
      </div>
    </div>
  );
}
